import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReportModal from "../../../components/ReportModal/ReportModal";
import { bloquearUsuario } from "../../../utils/bloqueos";
import {
  Avatar,
  Button,
  Card,
  Carousel,
  Col,
  Flex,
  List,
  Row,
  Rate,
  Typography,
  InputNumber,
  Skeleton,
  Space,
  Divider,
  Alert,
  Result,
} from "antd";
import { db } from "../../../firebaseConfig";
import {
  doc,
  collection,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { useUser } from "../../../context/UserContext";
import { Toaster, toast } from "sonner";
import {
  HeartOutlined,
  HeartFilled,
  ShoppingCartOutlined,
  SafetyCertificateOutlined,
  RocketOutlined,
  DownOutlined,
  UpOutlined,
  CheckCircleOutlined,
  LockOutlined,
  ThunderboltOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import visaMaster from "../../../assets/visa-mastercard.png";
import openpayLogo from "../../../assets/openpay.png";
import { addToCartProductoDetalle } from "../../../utils/productos/productoUtils";
import { handleFavoriteProductoAction } from "../../../utils/favoritos/favoritosUtils";
import { obtenerNombreCategoria } from "../../../utils/categorias/categoriaUtils";
import "./Producto.scss";
import { MODERACION_ACTIVA } from "../../../config";

const { Title, Paragraph, Text } = Typography;

export default function Producto() {
  const { id } = useParams();
  const [producto, setProducto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cantidad, setCantidad] = useState(1);
  const [reseñas, setReseñas] = useState([]);
  const [reporte, setReporte] = useState(null);
  const [calificacionPromedio, setCalificacionPromedio] = useState(0);
  const { userData, userFavorites } = useUser();
  const [isFavorite, setIsFavorite] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [tienda, setTienda] = useState(null);
  const [nombreCategoria, setNombreCategoria] = useState("");
  const [expandedDesc, setExpandedDesc] = useState(false); // Estado para ver más/menos descripción
  const [activeImg, setActiveImg] = useState(0);
  const [stickyVisible, setStickyVisible] = useState(false);
  const buyButtonRef = React.useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (userFavorites?.productos?.length > 0) {
      setIsFavorite(userFavorites.productos.includes(id));
    }
  }, [id, userFavorites, userData]);

  useEffect(() => {
    const getFullData = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, "productos", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const productData = { id: docSnap.id, ...docSnap.data() };
          setProducto(productData);

          // Obtener Tienda
          const tiendaRef = doc(db, "tiendas", productData.vendedorUid);
          const tiendaSnap = await getDoc(tiendaRef);
          if (tiendaSnap.exists())
            setTienda({ id: tiendaSnap.id, ...tiendaSnap.data() });
        }
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    getFullData();
    obtenerReseñas();
  }, [id]);

  useEffect(() => {
    if (producto?.categoria) {
      obtenerNombreCategoria(producto.categoria)
        .then((nombre) => setNombreCategoria(nombre))
        .catch((error) => console.error("Error al obtener categoría:", error));
    }
  }, [producto]);

  const handleToggleFavorite = () => {
    if (!buttonDisabled) {
      // No disponible: solo se permite quitarlo de favoritos, no agregarlo
      if (noSePuedeComprar && !isFavorite) {
        toast.error(`${motivoNoDisponible} No se puede agregar a favoritos.`);
        return;
      }
      setIsFavorite(!isFavorite);
      setButtonDisabled(true);
      const action = isFavorite ? "remove" : "add";
      handleFavoriteProductoAction(id, action, userData);
      setTimeout(() => setButtonDisabled(false), 1000);
    }
  };

  // Lógica de Scroll para Barra Sticky
  useEffect(() => {
    const handleScroll = () => {
      if (buyButtonRef.current) {
        const rect = buyButtonRef.current.getBoundingClientRect();
        // Mostrar si el botón ya no es visible en la parte superior
        setStickyVisible(rect.bottom < 0);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const obtenerReseñas = async () => {
    try {
      const resenasCollection = collection(db, "resenas");
      const q = query(resenasCollection, where("idProducto", "==", id));
      const querySnapshot = await getDocs(q);
      const resenasData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setReseñas(resenasData);
      const promedio =
        resenasData.reduce((t, r) => t + r.calificacion, 0) /
          resenasData.length || 0;
      setCalificacionPromedio(promedio);
    } catch (e) {
      console.error(e);
    }
  };

  // Un producto pausado, de una tienda bloqueada o agotado no se puede comprar
  const motivoNoDisponible = !producto
    ? null
    : producto.pausa || producto.deshabilitado || producto.eliminado
    ? "Este producto ya no está disponible."
    : tienda && (tienda.activado === false || tienda.pausa || tienda.blacklist)
    ? "La tienda de este producto no está activa en este momento."
    : !(producto.stock > 0)
    ? "Este producto está agotado."
    : null;
  const noSePuedeComprar = !!motivoNoDisponible;

  const agregarAlCarrito = () => {
    if (noSePuedeComprar) {
      toast.error(motivoNoDisponible);
      return;
    }
    addToCartProductoDetalle(producto, userData, cantidad);
  };

  if (loading)
    return (
      <div className="product-detail-container">
        <Row gutter={[32, 32]} className="main-product-row">
          <Col xs={24} md={12} lg={14}>
            <div
              style={{
                height: "100%",
                minHeight: "400px",
                background: "#fafafa",
                borderRadius: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Skeleton.Image active style={{ transform: "scale(1.5)" }} />
            </div>
          </Col>
          <Col xs={24} md={12} lg={10}>
            <div style={{ paddingLeft: "20px", width: "100%" }}>
              <Skeleton.Input
                active
                size="small"
                style={{ width: 100, marginBottom: 15 }}
              />
              <Skeleton active title={{ width: "80%" }} paragraph={false} />
              <div style={{ margin: "25px 0" }}>
                <Skeleton.Button
                  active
                  size="large"
                  style={{ width: 200, height: 50 }}
                />
              </div>
              <Skeleton active paragraph={{ rows: 3 }} />
              <div style={{ margin: "20px 0", display: "flex", gap: "10px" }}>
                <Skeleton.Input active size="default" style={{ width: 80 }} />
                <Skeleton.Input active size="default" style={{ width: 150 }} />
              </div>
              <Skeleton.Button
                active
                block
                style={{ height: 60, borderRadius: "12px", marginTop: 10 }}
              />
              <div style={{ marginTop: 20 }}>
                <Skeleton active avatar paragraph={{ rows: 1 }} />
              </div>
            </div>
          </Col>
        </Row>
      </div>
    );

  if (!producto)
    return (
      <div className="product-detail-container">
        <Result
          status="404"
          title="Este producto ya no existe"
          subTitle="Pudo haber sido eliminado por el vendedor."
          extra={
            <Button type="primary" onClick={() => navigate("/")}>
              Explorar productos
            </Button>
          }
        />
      </div>
    );

  return (
    <div className="product-detail-container">
      {/* BARRA STICKY SUPERIOR */}
      <div className={`sticky-buy-bar ${stickyVisible ? "visible" : ""}`}>
        <div className="sticky-container">
          <div className="product-compact-info">
            <img src={producto?.imagen?.[0]?.url} alt={producto?.nombre} />
            <div className="text-info">
              <Text strong>{producto?.nombre}</Text>
              <Text type="secondary" size="small">
                {nombreCategoria}
              </Text>
            </div>
          </div>
          <div className="sticky-actions">
            <Text className="price-compact">${producto?.total} MXN</Text>
            <Button
              type="primary"
              className="sticky-add-btn"
              disabled={noSePuedeComprar}
              onClick={agregarAlCarrito}
            >
              Agregar
            </Button>
          </div>
        </div>
      </div>

      <Toaster richColors position="top-center" />

      <Row gutter={[32, 0]} className="main-product-row">
        <button className="back-link" onClick={() => navigate(-1)}>
          <ArrowLeftOutlined /> Volver
        </button>
        {/* COLUMNA IZQUIERDA: IMÁGENES CON MINIATURAS */}
        <Col xs={24} md={12} lg={14}>
          <div className="gallery-layout">
            <div className="thumbnails-container">
              {producto?.imagen.map((img, i) => (
                <div
                  key={i}
                  className={`thumbnail-item ${activeImg === i ? "active" : ""}`}
                  onMouseEnter={() => setActiveImg(i)}
                  onClick={() => setActiveImg(i)}
                >
                  <img src={img.url} alt={`Miniatura ${i}`} />
                </div>
              ))}
            </div>

            <div className="main-carousel-container">
              <div className="carousel-wrapper">
                <Carousel
                  autoplay={false}
                  dots={false}
                  ref={(node) => {
                    if (node) node.goTo(activeImg);
                  }}
                >
                  {producto?.imagen.map((img, i) => (
                    <div key={i} className="img-slide">
                      <img src={img.url} alt={producto.nombre} />
                    </div>
                  ))}
                </Carousel>
                <Button
                  className="fav-btn-float"
                  shape="circle"
                  icon={
                    isFavorite ? (
                      <HeartFilled style={{ color: "#ff4d4f" }} />
                    ) : (
                      <HeartOutlined />
                    )
                  }
                  onClick={handleToggleFavorite}
                />
              </div>
            </div>
          </div>
        </Col>

        {/* COLUMNA DERECHA: INFO Y COMPRA */}
        <Col xs={24} md={12} lg={10}>
          <div className="product-info-card">
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              {nombreCategoria ? (
                <Text type="secondary" className="category-tag" uppercase>
                  {nombreCategoria}
                </Text>
              ) : (
                <Skeleton.Input
                  active
                  size="small"
                  style={{ width: 100, height: 20 }}
                />
              )}

              <Title level={2} className="product-title">
                {producto?.nombre}
              </Title>

              <Flex align="center" gap={8} className="rating-container">
                <Rate
                  disabled
                  allowHalf
                  value={calificacionPromedio}
                  style={{ fontSize: 16 }}
                />
                <Text type="secondary">({reseñas.length} reseñas)</Text>
              </Flex>
              {userData?.uid && (
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0 }}
                  onClick={() => setReporte({ tipo: "producto", targetId: id, targetUid: producto?.vendedorUid, contexto: producto?.nombre })}
                >
                  Reportar este producto
                </Button>
              )}

              <div className="price-tag">
                <Text className="currency">$</Text>
                <Text className="amount">{producto?.total}</Text>
                <Text className="mxn">MXN</Text>
              </div>

              {/* BADGES DE URGENCIA */}
              <div style={{ marginTop: 10 }}>
                <div className="urgency-badge">
                  <RocketOutlined /> Envío Express disponible
                </div>
                {producto?.stock <= 5 && (
                  <div className="urgency-badge stock-low">
                    <ThunderboltOutlined /> ¡Últimas {producto.stock} unidades!
                  </div>
                )}
              </div>

              {/* DESCRIPCIÓN CON EFECTO VER MÁS */}
              <div
                className={`description-wrapper ${
                  expandedDesc ? "expanded" : ""
                }`}
                onClick={() => setExpandedDesc(!expandedDesc)}
              >
                <Paragraph className="description-text">
                  {producto?.descripcion}
                </Paragraph>
                <div className="read-more-trigger">
                  {expandedDesc ? (
                    <span>
                      Ocultar <UpOutlined style={{ fontSize: 10 }} />
                    </span>
                  ) : (
                    <span>
                      Ver más <DownOutlined style={{ fontSize: 10 }} />
                    </span>
                  )}
                </div>
              </div>

              <Card size="small" className="shipping-info-card">
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Flex align="center" gap={10}>
                    <div
                      style={{
                        background: "#f0fdf4",
                        color: "#1dbf73",
                        padding: "8px",
                        borderRadius: "8px",
                      }}
                    >
                      <RocketOutlined />
                    </div>
                    <div>
                      <Text strong style={{ display: "block" }}>
                        Envío a todo el país
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Llega en 2-4 días hábiles
                      </Text>
                    </div>
                  </Flex>
                  <Divider style={{ margin: "8px 0" }} />
                  <Flex align="center" gap={10}>
                    <div
                      style={{
                        background: "#eff6ff",
                        color: "#3b82f6",
                        padding: "8px",
                        borderRadius: "8px",
                      }}
                    >
                      <SafetyCertificateOutlined />
                    </div>
                    <div>
                      <Text strong style={{ display: "block" }}>
                        Compra Garantizada
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        15 días de devolución gratis
                      </Text>
                    </div>
                  </Flex>
                </Space>
              </Card>

              <div className="purchase-actions" ref={buyButtonRef}>
                {noSePuedeComprar && (
                  <Alert
                    type="warning"
                    showIcon
                    message={motivoNoDisponible}
                    description="Por ahora no puedes comprarlo. Puedes quitarlo de favoritos o buscar uno parecido."
                    style={{ marginBottom: 16, borderRadius: 12 }}
                  />
                )}
                <Text strong>Cantidad:</Text>
                <Flex
                  gap={12}
                  align="center"
                  style={{ marginTop: 8, marginBottom: 16 }}
                >
                  <InputNumber
                    min={1}
                    max={producto?.stock}
                    defaultValue={1}
                    onChange={setCantidad}
                    style={{ borderRadius: "8px" }}
                  />
                  <Text type="secondary">({producto?.stock} disponibles)</Text>
                </Flex>

                <Button
                  type="primary"
                  size="large"
                  block
                  icon={<ShoppingCartOutlined />}
                  className="add-to-cart-btn"
                  disabled={noSePuedeComprar}
                  onClick={agregarAlCarrito}
                >
                  {noSePuedeComprar
                    ? producto?.stock > 0
                      ? "No disponible"
                      : "Agotado"
                    : "Agregar al carrito"}
                </Button>
              </div>

              {/* SECCIÓN DE CONFIANZA Y PAGOS */}
              <div className="trust-signals-container">
                <div className="trust-item">
                  <div className="icon-box">
                    <LockOutlined />
                  </div>
                  <Text>Pago 100% seguro</Text>
                </div>
                <div className="trust-item">
                  <div className="icon-box">
                    <CheckCircleOutlined />
                  </div>
                  <Text>Calidad Verificada</Text>
                </div>
                <div className="payment-logos">
                  <img src={visaMaster} alt="Visa y Mastercard" />
                  <img src={openpayLogo} alt="Openpay" />
                </div>
              </div>

              {tienda && (
                <Link to={`/reseñas/${tienda.id}`} className="seller-link">
                  <Card
                    size="small"
                    hoverable
                    style={{ marginTop: 20, borderRadius: 12 }}
                  >
                    <Flex align="center" gap={12}>
                      <Avatar src={tienda.photo} size="large" />
                      <div>
                        <Text strong style={{ display: "block" }}>
                          Vendido por: {tienda.nombre}
                        </Text>
                        <Rate
                          disabled
                          value={tienda.calificacion}
                          style={{ fontSize: 12 }}
                        />
                      </div>
                    </Flex>
                  </Card>
                </Link>
              )}
            </Space>
          </div>
        </Col>
      </Row>

      {/* SECCIÓN DE RESEÑAS */}
      <div className="reviews-section">
        <Row gutter={[48, 32]}>
          <Col xs={24} lg={8}>
            <div className="reviews-summary-card">
              <Title level={3}>Opiniones del producto</Title>
              <Flex align="center" gap={16} className="average-container">
                <h2 className="average-number">
                  {calificacionPromedio.toFixed(1)}
                </h2>
                <div className="average-info">
                  <Rate disabled allowHalf value={calificacionPromedio} />
                  <Text type="secondary" block>
                    {reseñas.length} calificaciones
                  </Text>
                </div>
              </Flex>

              <div className="stars-distribution">
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = reseñas.filter(
                    (r) => Math.round(r.calificacion) === star
                  ).length;
                  const percentage =
                    reseñas.length > 0 ? (count / reseñas.length) * 100 : 0;
                  return (
                    <Flex
                      key={star}
                      align="center"
                      gap={8}
                      className="star-row"
                    >
                      <Text style={{ minWidth: "60px" }}>{star} estrellas</Text>
                      <div className="progress-bar-bg">
                        <div
                          className="progress-bar-fill"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <Text type="secondary" style={{ minWidth: "30px" }}>
                        {count}
                      </Text>
                    </Flex>
                  );
                })}
              </div>

              <Divider />

            </div>
          </Col>

          <Col xs={24} lg={16}>
            <List
              itemLayout="vertical"
              dataSource={reseñas.filter((r) => !(userData?.bloqueados || []).includes(r.idUser))}
              pagination={{ pageSize: 5 }}
              locale={{
                emptyText: "Aún no hay reseñas. ¡Sé el primero en calificar!",
              }}
              renderItem={(item) => (
                <div className="review-card-pro">
                  <Flex gap={12} align="start">
                    <Avatar
                      src={item.photo}
                      size={48}
                      className="review-avatar"
                    />
                    <div style={{ flex: 1 }}>
                      <Flex justify="space-between" align="center">
                        <Text strong className="review-author">
                          {item.nombre.split("@")[0]}
                        </Text>
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          Compra verificada
                        </Text>
                      </Flex>
                      <Rate
                        disabled
                        defaultValue={item.calificacion}
                        style={{ fontSize: 12, display: "block" }}
                      />
                      <Paragraph
                        className="review-text"
                        style={{ marginTop: 8 }}
                      >
                        {item.descripcion}
                      </Paragraph>
                      {MODERACION_ACTIVA && userData?.uid && item.idUser !== userData.uid && (
                        <Flex gap={12}>
                          <Button
                            type="link"
                            size="small"
                            style={{ padding: 0 }}
                            onClick={() => setReporte({ tipo: "resena", targetId: item.id, targetUid: item.idUser, contexto: item.descripcion })}
                          >
                            Reportar
                          </Button>
                          <Button
                            type="link"
                            size="small"
                            danger
                            style={{ padding: 0 }}
                            onClick={() => bloquearUsuario(userData.uid, item.idUser).then(() => toast.success("Usuario bloqueado"))}
                          >
                            Bloquear usuario
                          </Button>
                        </Flex>
                      )}
                    </div>
                  </Flex>
                </div>
              )}
            />
          </Col>
        </Row>

        {MODERACION_ACTIVA && (
          <ReportModal
            open={!!reporte}
            onClose={() => setReporte(null)}
            tipo={reporte?.tipo}
            targetId={reporte?.targetId}
            targetUid={reporte?.targetUid}
            contexto={reporte?.contexto}
          />
        )}
      </div>
    </div>
  );
}